import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
  FILTER_POSTAL_CODE,
  FILTER_LOCATION_NAME,
  LOCATION_REDIRECT
} from 'dpl/constants/query_params';
import {
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEW_FEATURE_LOCATIONS
} from 'dpl/constants/analytics';
import { RESULTS_TYPES } from 'dpl/puppy_search_results/utils/constants';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import { truncateText } from 'dpl/shared/utils';
import { isNumeric } from 'dpl/shared/form/utils';

import FilterPill from './FilterPill';
import LocationFilter from './LocationFilter';
import ExposedLocationFilterBottomModal from './ExposedLocationFilterBottomModal';
import ExposedLocationFilterDropdown from './ExposedLocationFilterDropdown';

const IS_LT_MD = isLessThanBreakpoint('md');
const FILTER_TITLE = 'Filter results by location';

export default function LocationFilterButton({ resultsType, totalItemCount }) {
  const [isOpen, setIsOpen] = useState(false);

  const params = useSelector(({ queryParams }) => queryParams);
  const isSelected = Boolean(
    params[FILTER_POSTAL_CODE] || params[FILTER_LOCATION_NAME]
  );

  const filterButtonProps = {
    resultsType,
    totalItemCount,
    setIsMenuOpen: IS_LT_MD ? setIsOpen : () => setIsOpen(false)
  };

  const analytics = {
    button_cta: 'location',
    view:
      resultsType === RESULTS_TYPES.PUPPIES
        ? ANALYTICS_VIEWS.PUPPY_SEARCH_RESULTS
        : ANALYTICS_VIEWS.BREEDER_SEARCH_RESULTS,
    view_feature: ANALYTICS_VIEW_FEATURES.LOCATION_FILTER_BUTTON,
    view_feature_location: ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_HEADER
  };

  const locationLabel = useSelector(({ queryParams, server }) => {
    const locationText =
      queryParams[FILTER_POSTAL_CODE] ||
      queryParams[FILTER_LOCATION_NAME] ||
      server.locationTitle ||
      'Location';

    // Zip code autopopulated
    if (
      queryParams[LOCATION_REDIRECT] === 'true' &&
      isNumeric(queryParams[FILTER_POSTAL_CODE])
    ) {
      return 'Location';
    }

    const lastFiveChars = locationText.slice(-5);

    // Zip code entered by user
    if (isNumeric(lastFiveChars)) {
      return lastFiveChars;
    }

    if (IS_LT_MD) {
      return truncateText(locationText, 15);
    }

    return locationText;
  });

  const FilterButton = (
    <FilterPill
      onClick={() => setIsOpen(true)}
      isSelected={isSelected}
      label={locationLabel}
      displayDropdownArrow
      prefixIconName="fetch-location"
      isOpen={isOpen}
      analytics={analytics}
    />
  );

  if (IS_LT_MD) {
    return (
      <>
        {FilterButton}
        <ExposedLocationFilterBottomModal
          isFilterMenuOpen={isOpen}
          setIsFilterMenuOpen={setIsOpen}
          title={FILTER_TITLE}
        >
          <LocationFilter
            resultsType={resultsType}
            isOpen={isOpen}
            filterButtonProps={filterButtonProps}
          />
        </ExposedLocationFilterBottomModal>
      </>
    );
  }

  return (
    <ExposedLocationFilterDropdown
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      toggleButton={FilterButton}
      title={FILTER_TITLE}
    >
      <LocationFilter
        inputClassName="w-100"
        resultsType={resultsType}
        isOpen={isOpen}
        filterButtonProps={filterButtonProps}
      />
    </ExposedLocationFilterDropdown>
  );
}
LocationFilterButton.propTypes = {
  resultsType: PropTypes.string.isRequired,
  totalItemCount: PropTypes.number.isRequired
};
